import { defineStore } from 'pinia'
import apiService, { API_ROOT } from '@/services/apiService'
import { saveConfigToStorage } from '@/services/configStorage'

export const useTrailerStore = defineStore('trailer', {
    state: () => ({
        trailers: [],
        trailersGrouped: {},
        addonsForSelectedTrailerModel: [],
        moreDetailedTrailerAddons: [],
        addonsForModels: [],
        addons: [],
        loading: false,
        error: null,
        selectedTrailer: null,
        selectedTrailerAddons: [],
        selectedTrailerAddonsPriceSum: 0,
        trailerGroups: [],
        trailerGroupsLoading: false,
    }),

    actions: {
        // ======== TRAILERS ========
        async fetchTrailers() {
            this.loading = true
            this.error = null

            try {
                const response = await apiService.get('/trailers')

                this.trailers = response.data.map(trailer => ({
                    ...trailer,
                    image_url: trailer.image ? `${API_ROOT}/storage/${trailer.image}` : '',
                    technical_drawing_url: trailer.technical_drawing ? `${API_ROOT}/storage/${trailer.technical_drawing}` : ''
                }))

                const grouped = {}
                this.trailers.forEach(trailer => {
                    const material = trailer.trailer_group?.material || 'Inne'
                    const group = trailer.trailer_group?.name || 'Brak grupy'

                    if (!grouped[material]) grouped[material] = {}
                    if (!grouped[material][group]) grouped[material][group] = []

                    grouped[material][group].push(trailer)
                })

                this.trailersGrouped = grouped
            } catch (err) {
                this.error = err
                console.error('Błąd przy pobieraniu lawet:', err)
            } finally {
                this.loading = false
            }
        },

        async createTrailer(formData) {
            this.loading = true
            this.error = null

            try {
                const response = await apiService.post('/trailers', formData)
                const trailer = response.data

                trailer.image_url = trailer.image ? `${API_ROOT}/storage/${trailer.image}` : ''
                trailer.technical_drawing_url = trailer.technical_drawing ? `${API_ROOT}/storage/${trailer.technical_drawing}` : ''

                this.trailers.push(trailer)

                return trailer
            } catch (err) {
                this.error = err
                console.error('Błąd przy tworzeniu lawety:', err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async updateTrailer(id, formData) {
            this.loading = true
            this.error = null

            try {
                const response = await apiService.post(`/trailers/${id}`, formData)
                const updated = response.data

                updated.image_url = updated.image ? `${API_ROOT}/storage/${updated.image}` : ''
                updated.technical_drawing_url = updated.technical_drawing ? `${API_ROOT}/storage/${updated.technical_drawing}` : ''

                const index = this.trailers.findIndex(t => t.id === id)
                if (index !== -1) this.trailers[index] = updated

                return updated
            } catch (err) {
                this.error = err
                console.error('Błąd przy aktualizacji lawety:', err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async deleteTrailer(id) {
            this.loading = true
            this.error = null

            try {
                await apiService.post(`/trailers-delete/${id}`)
                this.trailers = this.trailers.filter(t => t.id !== id)
            } catch (err) {
                this.error = err
                console.error('Błąd przy usuwaniu lawety:', err)
                throw err
            } finally {
                this.loading = false
            }
        },

        async selectTrailer(trailer) {
            this.selectedTrailer = trailer

            this.updateAddonsForSelectedTrailerModel()
            this.updateMoreDetailedTrailerAddons()

            const defaults = this.moreDetailedTrailerAddons.filter(a => a.is_default)
            this.updateSelectedAddons(defaults)
        },

        async fetchAddonsForModels() {
            try {
                const response = await apiService.get('/addons-for-model')
                this.addonsForModels = response.data
            } catch (err) {
                this.error = err
                console.error('Błąd przy pobieraniu kompatybilnych dodatków:', err)
            } finally {
                this.loading = false
            }
        },

        updateAddonsForSelectedTrailerModel() {
            if (!this.selectedTrailer) {
                this.addonsForSelectedTrailerModel = []
                return
            }

            this.addonsForSelectedTrailerModel = this.addonsForModels.filter(
                addon => addon.trailer_id === this.selectedTrailer.id
            )
        },

        async fetchAddons() {
            try {
                const response = await apiService.get('/addons')
                this.addons = response.data.map(addon => ({
                    ...addon,
                    image_url: addon.image ? `${API_ROOT}/storage/${addon.image}` : ''
                }))
            } catch (err) {
                this.error = err
                console.error('Błąd przy pobieraniu dodatków:', err)
            } finally {
                this.loading = false
            }
        },

        updateMoreDetailedTrailerAddons() {
            if (!this.addonsForSelectedTrailerModel.length || !this.addons.length) {
                this.moreDetailedTrailerAddons = []
                return
            }

            const unsorted = this.addonsForSelectedTrailerModel
                .map(afm => {
                    const fullAddon = this.addons.find(a => a.id === afm.addon_id)
                    if (!fullAddon) return null

                    return {
                        ...fullAddon,
                        group: fullAddon.group || 'Brak kategorii',
                        price: Math.round(parseFloat(afm.price) * 100),
                        is_default: afm.is_default === 1
                    }
                })
                .filter(Boolean)

            this.moreDetailedTrailerAddons = unsorted
        },

        updateSelectedAddons(selectedAddonObjects) {
            this.selectedTrailerAddons = selectedAddonObjects

            this.selectedTrailerAddonsPriceSum = selectedAddonObjects.reduce(
                (sum, addon) => sum + addon.price,
                0
            )

            if (this.selectedTrailer) {
                saveConfigToStorage({
                    trailer: this.selectedTrailer,
                    addons: selectedAddonObjects
                })
            }
        },

        formatPrice(price) {
            return (price / 100)
                .toFixed(2)
                .replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        },

        // ======== TRAILER GROUPS ========

        async fetchTrailerGroups() {
            this.trailerGroupsLoading = true
            this.error = null

            try {
                const response = await apiService.get('/trailer-groups')
                this.trailerGroups = response.data
            } catch (err) {
                this.error = err
                console.error('Błąd przy pobieraniu grup lawet:', err)
            } finally {
                this.trailerGroupsLoading = false
            }
        },

        async createTrailerGroup(data) {
            try {
                const response = await apiService.post('/trailer-groups', data)
                this.trailerGroups.push(response.data)
            } catch (err) {
                this.error = err
                console.error('Błąd przy tworzeniu grupy lawet:', err)
                throw err
            }
        },

        async updateTrailerGroup(id, data) {
            try {
                const response = await apiService.post(`/trailer-groups/${id}`, data)
                const index = this.trailerGroups.findIndex(group => group.id === id)
                if (index !== -1) this.trailerGroups[index] = response.data
            } catch (err) {
                this.error = err
                console.error('Błąd przy aktualizacji grupy lawet:', err)
                throw err
            }
        },

        async deleteTrailerGroup(id) {
            try {
                await apiService.post(`/trailer-groups-delete/${id}`)
                this.trailerGroups = this.trailerGroups.filter(group => group.id !== id)
            } catch (err) {
                this.error = err
                console.error('Błąd przy usuwaniu grupy lawet:', err)
                throw err
            }
        },

        getAddonNameById(id) {
            const addon = this.addons.find(a => String(a.id) === String(id))
            return addon ? (addon.desc || addon.code || 'brak nazwy dodatku') : 'brak nazwy dodatku'
        },

        getTrailerNameById(id) {
            const trailer = this.trailers.find(t => String(t.id) === String(id))
            return trailer ? trailer.name : 'brak nazwy lawety'
        },
    }
})